import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header1 from './Header/Header1';
import { useAuth } from "../AuthContext";

/* ── inline styles as CSS variables via a style tag injected once ── */
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

    .pj-root {
      min-height: 100vh;
      background: #080808;
      color: #fff;
      font-family: 'DM Sans', sans-serif;
    }

    .pj-bg {
      position: fixed; inset: 0; z-index: 0; pointer-events: none;
      background:
        radial-gradient(ellipse 60% 50% at 70% 20%, rgba(37,99,235,0.12) 0%, transparent 70%),
        radial-gradient(ellipse 40% 40% at 20% 80%, rgba(99,37,235,0.08) 0%, transparent 70%);
    }

    .pj-wrap {
      position: relative; z-index: 1;
      padding-top: 90px;
      display: flex; flex-direction: column; align-items: center;
      gap: 0;
    }

    /* ── CARD ── */
    .pj-card {
      width: min(680px, 92vw);
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.07);
      border-radius: 24px;
      overflow: hidden;
      backdrop-filter: blur(12px);
      margin-top: 32px;
    }

    /* ── VIDEO AREA ── */
    .pj-video-wrap {
      position: relative;
      width: 100%; aspect-ratio: 16/9;
      background: #0d0d0d;
      overflow: hidden;
    }
    .pj-video-wrap video {
      width: 100%; height: 100%; object-fit: cover;
      display: block;
    }

    /* subtle scan-line overlay for cinematic feel */
    .pj-video-wrap::after {
      content: '';
      position: absolute; inset: 0; pointer-events: none;
      background: repeating-linear-gradient(
        0deg,
        rgba(0,0,0,0) 0px,
        rgba(0,0,0,0) 3px,
        rgba(0,0,0,0.06) 3px,
        rgba(0,0,0,0.06) 4px
      );
    }

    /* live badge */
    .pj-live-badge {
      position: absolute; top: 16px; left: 16px; z-index: 2;
      display: flex; align-items: center; gap: 6px;
      background: rgba(0,0,0,0.55); border: 1px solid rgba(255,255,255,0.1);
      backdrop-filter: blur(6px);
      padding: 5px 12px; border-radius: 99px;
      font-size: 11px; font-weight: 600; letter-spacing: 1.5px;
      color: #a3a3a3; font-family: 'Syne', sans-serif; text-transform: uppercase;
    }
    .pj-live-dot {
      width: 7px; height: 7px; border-radius: 50%; background: #4ade80;
      animation: pulse-dot 2s ease-in-out infinite;
    }
    @keyframes pulse-dot {
      0%,100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.85); }
    }

    /* name tag */
    .pj-name-tag {
      position: absolute; bottom: 16px; left: 16px; z-index: 2;
      background: rgba(0,0,0,0.6); border: 1px solid rgba(255,255,255,0.08);
      backdrop-filter: blur(8px);
      padding: 6px 14px; border-radius: 99px;
      font-size: 13px; font-weight: 500; letter-spacing: 0.2px;
    }

    /* avatar fallback */
    .pj-avatar {
      width: 80px; height: 80px; border-radius: 50%;
      background: linear-gradient(135deg, #2563eb, #7c3aed);
      display: flex; align-items: center; justify-content: center;
      font-size: 30px; font-weight: 700; font-family: 'Syne', sans-serif;
      box-shadow: 0 0 40px rgba(37,99,235,0.35);
    }
    .pj-avatar-center {
      width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; gap: 12px;
    }
    .pj-avatar-label {
      font-size: 14px; color: rgba(255,255,255,0.45); font-weight: 400;
    }

    /* cam-off dim overlay */
    .pj-cam-off {
      position: absolute; inset: 0; z-index: 1;
      background: rgba(0,0,0,0.75);
      display: flex; align-items: center; justify-content: center;
      flex-direction: column; gap: 12px;
    }

    /* ── BOTTOM PANEL ── */
    .pj-panel {
      padding: 24px 28px 28px;
      display: flex; flex-direction: column; gap: 20px;
      border-top: 1px solid rgba(255,255,255,0.05);
    }

    /* ── TOGGLE ROW ── */
    .pj-toggle-row {
      display: flex; gap: 10px;
    }

    .pj-toggle {
      flex: 1;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 10px 0; border-radius: 12px; border: none; cursor: pointer;
      font-size: 13px; font-weight: 500; font-family: 'DM Sans', sans-serif;
      letter-spacing: 0.2px;
      transition: all 0.18s ease;
    }
    .pj-toggle.on  { background: rgba(74,222,128,0.1); color: #4ade80; border: 1px solid rgba(74,222,128,0.25); }
    .pj-toggle.off { background: rgba(248,113,113,0.08); color: #f87171; border: 1px solid rgba(248,113,113,0.2); }
    .pj-toggle:hover { filter: brightness(1.12); transform: translateY(-1px); }

    /* toggle icon dot */
    .pj-toggle-icon {
      width: 8px; height: 8px; border-radius: 50%;
      background: currentColor;
    }

    /* ── DEVICE SELECTS ── */
    .pj-device-row {
      display: flex; gap: 10px;
    }
    .pj-select-wrap {
      flex: 1; position: relative;
    }
    .pj-select-wrap::after {
      content: '▾';
      position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
      font-size: 11px; color: rgba(255,255,255,0.35); pointer-events: none;
    }
    .pj-select {
      width: 100%; padding: 9px 32px 9px 12px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 10px; color: rgba(255,255,255,0.7);
      font-size: 12.5px; font-family: 'DM Sans', sans-serif;
      appearance: none; outline: none; cursor: pointer;
      transition: border-color 0.15s;
    }
    .pj-select:hover  { border-color: rgba(255,255,255,0.18); }
    .pj-select:focus  { border-color: rgba(37,99,235,0.6); }
    .pj-select option { background: #1a1a1a; }

    /* ── JOIN BUTTON ── */
    .pj-join-btn {
      width: 100%; padding: 14px 0; border-radius: 14px; border: none;
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      color: #fff; font-size: 15px; font-weight: 600;
      font-family: 'Syne', sans-serif; letter-spacing: 0.3px;
      cursor: pointer; position: relative; overflow: hidden;
      box-shadow: 0 4px 24px rgba(37,99,235,0.35);
      transition: all 0.2s ease;
    }
    .pj-join-btn::before {
      content: '';
      position: absolute; inset: 0;
      background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%);
    }
    .pj-join-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(37,99,235,0.45);
    }
    .pj-join-btn:active { transform: translateY(0); }

    /* ── AUDIENCE PILL ── */
    .pj-audience-note {
      text-align: center; font-size: 12.5px; color: rgba(255,255,255,0.3);
      padding: 4px 0;
    }

    /* ── DIVIDER ── */
    .pj-divider {
      height: 1px; background: rgba(255,255,255,0.05); margin: 0 -28px;
    }
  `}</style>
);

/* ── small SVG icons ── */
const MicIcon = ({ on }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {on
      ? <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></>
      : <><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></>
    }
  </svg>
);

const CamIcon = ({ on }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {on
      ? <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></>
      : <><line x1="1" y1="1" x2="23" y2="23"/><path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h1a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"/></>
    }
  </svg>
);

/* ══════════════════════════════════════════════════════════ */

const PreJoinScreen = () => {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId, logout } = useAuth();
  const hostName = searchParams.get("host_name");

// logged-in user (from API)

  const role    = searchParams.get("role");
  const channel = searchParams.get("channel");


  const [micOn, setMicOn] = useState(role === "host");
  const [camOn, setCamOn] = useState(role === "host");
  const [cameras, setCameras]       = useState([]);
  const [mics, setMics]             = useState([]);
  const [selectedCam, setSelectedCam] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [userName, setUserName] = useState("");
  const postId = searchParams.get("post_id");

useEffect(() => {
  if (!userId) return;

  fetch(`${process.env.REACT_APP_API_URL}/get_user_data/?user_id=${userId}`)
    .then(res => res.json())
    .then(data => {
      setUserName(data.name || "");
    })
    .catch(console.error);
}, [userId]);

  /* ─────────────── GET DEVICES ─────────────── */
const getDevices = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();

  const videoDevices = devices.filter(d => d.kind === "videoinput");
  const audioDevices = devices.filter(d => d.kind === "audioinput");

  console.log("Cameras:", videoDevices);
  console.log("Mics:", audioDevices);

  setCameras(videoDevices);
  setMics(audioDevices);

  if (videoDevices.length > 0) setSelectedCam(videoDevices[0].deviceId);
  if (audioDevices.length > 0) setSelectedMic(audioDevices[0].deviceId);
};
/* ─────────────── START PREVIEW (only on device change) ─────────────── */
/* ─────────────── START PREVIEW ─────────────── */
const startPreview = async (camId, micId, isCamOn, isMicOn) => {
  try {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (role !== "host") return;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: camId ? { deviceId: { exact: camId } } : true,
      audio: micId ? { deviceId: { exact: micId } } : true,
    });

    // ✅ Use passed-in values, not stale closure
    stream.getVideoTracks().forEach(t => t.enabled = isCamOn);
    stream.getAudioTracks().forEach(t => t.enabled = isMicOn);

    streamRef.current = stream;
    if (videoRef.current) videoRef.current.srcObject = stream;
    await getDevices();
  } catch (err) { console.error("Preview error:", err); }
};

/* ─────────────── INIT ─────────────── */


/* ─────────────── DEVICE CHANGE ONLY ─────────────── */
useEffect(() => {
  const init = async () => {
    try {
      if (role === "host") {
        // 🔥 STEP 1: ask permission FIRST
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        // 🔥 STEP 2: NOW devices will have labels
        await getDevices();
      }
    } catch (err) {
      console.error("Permission error:", err);
    }
  };

  init();

  return () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
  };
  // eslint-disable-next-line
}, []);

/* ─────────────── TOGGLES (unchanged) ─────────────── */
const handleMicToggle = () => {
  const next = !micOn;
  streamRef.current?.getAudioTracks().forEach(t => t.enabled = next);
  setMicOn(next);
};

const handleCamToggle = () => {
  const next = !camOn;
  streamRef.current?.getVideoTracks().forEach(t => t.enabled = next);

  // ✅ Re-kick the video element when turning camera ON
  // track.enabled = true re-enables the track but the <video> stays frozen
  // Re-assigning srcObject + play() forces it to render live frames again
  if (next && videoRef.current && streamRef.current) {
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
  }

  setCamOn(next);
};

  const handleJoin = () => {
    navigate(`/live/${channel}?role=${role}&host_name=${hostName}&post_id=${postId}`, {
      state: { micOn, camOn, selectedCam, selectedMic ,userName,   // ✅ logged-in user
      hostName  }
    });
  };

const initial = userName
  ? userName.charAt(0).toUpperCase()
  : "U";
  return (
    <>
      <GlobalStyle />
      <div className="pj-root">
        <div className="pj-bg" />
        <Header1 userId={userId} onLogout={logout} />

        <div className="pj-wrap">
          <div className="pj-card">

            {/* ── VIDEO AREA ── */}
            <div className="pj-video-wrap">
              {/* live indicator */}
              <div className="pj-live-badge">
                <span className="pj-live-dot" />
                Preview
              </div>

              {/* camera feed */}
              {role === "host" && (
                <video ref={videoRef} autoPlay muted playsInline />
              )}

              {/* cam-off overlay */}
              {(!camOn || role !== "host") && (
                <div className="pj-cam-off">
                  <div className="pj-avatar">{initial}</div>
                  <span className="pj-avatar-label">
                    {role === "audience" ? "Joining as viewer" : "Camera is off"}
                  </span>
                </div>
              )}

              {/* name tag */}
              <div className="pj-name-tag">
  {userName || "Loading..."}
</div>
            </div>

            {/* ── BOTTOM PANEL ── */}
            <div className="pj-panel">

          

  {role === "host" && (
    <div className="pj-instructions">
      <div className="pj-ins-title">Before you start</div>

      <div className="pj-ins-list">
        <div className="pj-ins-item">
          • Check your camera framing, lighting, and microphone before joining
        </div>

        <div className="pj-ins-item">
          • Keeping your camera or microphone on/off is completely optional
        </div>

        <div className="pj-ins-item">
          • You can use the screen share option anytime during the session to present your screen
        </div>

        <div className="pj-ins-item">
          • This session will be automatically recorded, so please allow the required permissions when prompted
        </div>

        <div className="pj-ins-item">
          • Once you press the leave button, the session will end for everyone
        </div>

        <div className="pj-ins-item">
          • Close unnecessary applications for a smoother experience
        </div>

        <div className="pj-ins-item">
          • Keep your content, presentation, or materials ready before going live
        </div>

        <div className="pj-ins-item">
          • In case of any technical issue, please contact contact@anthrasync.com
        </div>

        <div className="pj-ins-item">
          • All the best for your session 🚀
        </div>
      </div>
    </div>
  )}



              {/* host controls */}
              {role === "host" && (
                <>
                  {/* mic / cam toggles */}
                  <div className="pj-toggle-row">
                    <button className={`pj-toggle ${micOn ? "on" : "off"}`} onClick={handleMicToggle}>
  <MicIcon on={micOn} /> {micOn ? "Mic On" : "Mic Off"}
</button>
<button className={`pj-toggle ${camOn ? "on" : "off"}`} onClick={handleCamToggle}>
  <CamIcon on={camOn} /> {camOn ? "Camera On" : "Camera Off"}
</button>
                  </div>

                  {/* device selects */}
                  <div className="pj-device-row">
                    <div className="pj-select-wrap">
                      <select className="pj-select" value={selectedCam} onChange={e => setSelectedCam(e.target.value)}>
                        {cameras.map(c => <option key={c.deviceId} value={c.deviceId}>{c.label || "Camera"}</option>)}
                      </select>
                    </div>
                    <div className="pj-select-wrap">
                      <select className="pj-select" value={selectedMic} onChange={e => setSelectedMic(e.target.value)}>
                        {mics.map(m => <option key={m.deviceId} value={m.deviceId}>{m.label || "Microphone"}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="pj-divider" />
                </>
              )}

              {/* join button */}
              <button className="pj-join-btn" onClick={handleJoin}>
                {role === "host" ? "Start Session →" : "Join as Viewer →"}
              </button>

              {role === "audience" && (
                <p className="pj-audience-note">Mic and camera will remain off</p>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreJoinScreen;